import { Component, computed, effect, inject, input } from '@angular/core';
import { v4 as uuidv4 } from 'uuid';
import { environment } from 'environments';
import { RefillResponsesStoreStore } from '@refill-responses-store';
import { QuestionnaireAnswers } from '@types-lib';
import { calculateBMI, convertMetricToImperialHeightStr, convertMetricToImperialWeightStr } from '@shared-utils';
import { DatePipe } from '@angular/common';
import { AccordionComponent, AnswerDisplayComponent, ButtonComponent } from '@ui-components';
import { ServiceUpgradeInjectorService } from '@shims';

interface RefillsInputData {
  id?: string | number;
  [key: string]: unknown;
}

enum StatusLevel {
  Normal = 0,
  Information = 1,
  Warning = 2,
  Query = 2.5,
  Danger = 3,
}

@Component({
  selector: 'refills',
  imports: [AnswerDisplayComponent, DatePipe, AccordionComponent, ButtonComponent],
  templateUrl: './refills.component.html',
  styleUrls: [
    '../../../../../../../../../../libs/ui-components/src/lib/_styles/index.scss',
    './refills.component.scss',
  ],
})
export class RefillsComponent {
  visit = input<RefillsInputData>();
  visitId = computed(() => this.visit()?.id);
  store = inject(RefillResponsesStoreStore);
  private readonly serviceUpgradeInjectorService = inject(ServiceUpgradeInjectorService);
  accordionStates: boolean[] = [];

  refillResponsesDisplay = computed<QuestionnaireAnswers.RefillResponseDisplay[]>(() => {
    const data = this.store.refillResponses();
    const display = data?.map((answerData): QuestionnaireAnswers.RefillResponseDisplay => {
      return {
        medication: answerData.product_name,
        created: answerData.created,
        id: answerData.id,
        mailOrderFillRequestID: answerData.mail_order_fill_request_id,
        fillAnswerStatus: answerData.fill_answer_status.toLocaleLowerCase(),
        processedDate: answerData.processed_date,
        isOpen: answerData.status === 'open',
        answers: answerData.answers.map(ans => ({
          id: uuidv4(),
          question: ans.question.text,
          is_child: !!ans.question.display_condition,
          answer: this.fixDisplayAnswer(ans, ans.question),
        })),
      };
    });
    return display || [];
  });

  constructor() {
    effect(() => {
      const id = this.visitId() || '';
      this.store.setVisitId(id);
    });
  }

  fixDateDisplay(answer: QuestionnaireAnswers.DateValue): QuestionnaireAnswers.DisplayAnswer[] {
    return [
      {
        type: 'date',
        id: uuidv4(),
        text: new Date(answer.date),
        status: this.getStatusLabel(answer.status),
      },
    ];
  }

  fixDateHiddenDisplay(answer: QuestionnaireAnswers.DateHiddenValue): QuestionnaireAnswers.DisplayAnswer[] {
    return [
      {
        type: 'date_hidden',
        id: uuidv4(),
        text: answer.is_prescribed
          ? `Last prescribed ${answer.days_since} day${answer.days_since === 1 ? '' : 's'} ago.`
          : 'Unprocessed order.',
        additionalDetails: answer.date ? new Date(answer.date).toLocaleDateString('en-GB') : null,
        status: this.getStatusLabel(answer.status),
        tooltipText: !answer.is_prescribed ? 'Order/refill was requested but not dispensed.' : '',
      },
    ];
  }

  fixBMIDisplay(answer: QuestionnaireAnswers.BmiValue): QuestionnaireAnswers.DisplayAnswer[] {
    const system = answer.system === 'metric' ? 'METRIC' : 'IMPERIAL';
    const bmi = calculateBMI(answer.height, answer.weight, system);
    const imperialText = `${convertMetricToImperialHeightStr(answer.height)} / ${convertMetricToImperialWeightStr(answer.weight)} = ${bmi}`;
    const displayWeight = Math.trunc(answer.weight * 100) / 100;
    const metricText = `${answer.height}cm / ${displayWeight}kg = ${bmi}`;
    const status = this.getStatusLabel(answer.status);

    return [
      {
        type: 'bmi',
        id: uuidv4(),
        text: imperialText,
        status,
      },
      {
        type: 'bmi',
        id: uuidv4(),
        text: metricText,
        status,
      },
    ];
  }

  fixSelectDisplay(answers: QuestionnaireAnswers.SelectValue[]): QuestionnaireAnswers.DisplayAnswer[] {
    return answers.map(answer => ({
      type: 'select',
      id: uuidv4(),
      text: answer.text,
      status: this.getStatusLabel(answer.status),
      additionalDetails: answer.details || '',
    }));
  }

  fixDisplayAnswer(answer: QuestionnaireAnswers.Answer, question: QuestionnaireAnswers.Question) {
    const answersArray: QuestionnaireAnswers.DisplayAnswer[] = [];
    if (question.type === 'date') {
      const dateAnswer = answer.value as QuestionnaireAnswers.DateValue;
      answersArray.push(...this.fixDateDisplay(dateAnswer));
    }
    if (['bmi', 'bmi_weight'].includes(question.type)) {
      const bmiAnswer = answer.value as QuestionnaireAnswers.BmiValue;
      answersArray.push(...this.fixBMIDisplay(bmiAnswer));
    }
    if (question.type === 'select') {
      const selectAnswer = answer.value as QuestionnaireAnswers.SelectValue[];
      answersArray.push(...this.fixSelectDisplay(selectAnswer));
    }

    if (question.type === 'date_hidden') {
      const dateAnswer = answer.value as QuestionnaireAnswers.DateHiddenValue;
      answersArray.push(...this.fixDateHiddenDisplay(dateAnswer));
    }

    return answersArray;
  }

  getStatusLabel(code: StatusLevel): string {
    switch (code) {
      case StatusLevel.Normal:
        return 'normal';
      case StatusLevel.Information:
        return 'information';
      case StatusLevel.Warning:
        return 'warning';
      case StatusLevel.Query:
        return 'query';
      case StatusLevel.Danger:
        return 'danger';
      default:
        return 'unknown';
    }
  }

  switchTab(tabCode: string) {
    const $rootScope = this.serviceUpgradeInjectorService.getAngularJsRootScope();
    $rootScope.$broadcast('tab:changed', tabCode);
    $rootScope.$broadcast('mailorder:processPayment');
  }

  onAccordionToggle(index: number, isOpen: boolean) {
    this.accordionStates[index] = isOpen;
  }
}
